import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, errorMessage, isTransient } from "./api";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** Skip the global error toast (the caller renders the error inline). */
      silent?: boolean;
      /** Success toast text. */
      success?: string;
    };
  }
}

export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache(),
    mutationCache: new MutationCache({
      onError: (err, _vars, _ctx, mutation) => {
        if (mutation.meta?.silent) return;
        toast.error(errorMessage(err));
      },
      onSuccess: (_data, _vars, _ctx, mutation) => {
        if (mutation.meta?.success) toast.success(mutation.meta.success);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (count, err) => isTransient(err) && count < 2,
        retryDelay: (n) => Math.min(1000 * 2 ** n, 8000),
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();

/** Query keys. One place so invalidation stays consistent. */
export const qk = {
  me: ["me"] as const,
  platform: ["public", "platform"] as const,
  announcements: ["public", "announcements"] as const,
  catalog: ["catalog"] as const,
  flags: ["me", "flags"] as const,
  contacts: ["me", "contacts"] as const,
  sessions: ["auth", "sessions"] as const,
  sites: {
    all: ["sites"] as const,
    list: ["sites", "list"] as const,
    detail: (id: string) => ["sites", "detail", id] as const,
    draft: (id: string) => ["sites", "draft", id] as const,
    versions: (id: string) => ["sites", "versions", id] as const,
    version: (id: string, v: number) => ["sites", "version", id, v] as const,
    analytics: (id: string, days: number) => ["sites", "analytics", id, days] as const,
    orders: (id: string) => ["sites", "orders", id] as const,
    publishCheck: (id: string) => ["sites", "publish-check", id] as const,
    slug: (slug: string, id?: string) => ["sites", "slug", slug, id ?? ""] as const,
  },
  domains: {
    list: (siteId: string) => ["domains", siteId] as const,
    search: (q: string) => ["domains", "search", q] as const,
    dns: (siteId: string, domainId: string) => ["domains", siteId, domainId, "dns"] as const,
  },
  guests: (siteId: string) => ["guests", siteId] as const,
  wishes: (siteId: string) => ["wishes", siteId] as const,
  posts: {
    list: (siteId: string) => ["posts", siteId] as const,
    detail: (siteId: string, postId: string) => ["posts", siteId, postId] as const,
  },
  rsvps: (siteId: string) => ["rsvps", siteId] as const,
  assets: {
    list: (siteId: string) => ["assets", siteId] as const,
    usage: (siteId: string) => ["assets", "usage", siteId] as const,
  },
  ai: {
    job: (id: string) => ["ai", "job", id] as const,
    usage: (siteId: string) => ["ai", "usage", siteId] as const,
  },
  commerce: {
    quote: (siteId: string, items: unknown) => ["commerce", "quote", siteId, items] as const,
    orders: ["commerce", "orders"] as const,
    order: (id: string) => ["commerce", "order", id] as const,
    promos: ["commerce", "promos"] as const,
    promo: (code: string) => ["commerce", "promo", code] as const,
    claimQuote: (planId: string, voucher: string) => ["commerce", "claim-quote", planId, voucher] as const,
    credits: ["commerce", "credits"] as const,
    premium: (siteId: string) => ["commerce", "premium", siteId] as const,
  },
  library: ["library"] as const,
};

export const is404 = (err: unknown) => err instanceof ApiError && err.status === 404;
